import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { brandInfo } from '../data/locations';
import { schoolInfo } from '../data/mock';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { MapPin, Instagram, Send, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const ContactSection = ({ location = null, language = null, isFrenchLocation = false }) => {
  const { t: globalT } = useLanguage();
  const routerLocation = useLocation();
  const [formData, setFormData] = useState({
    parentName: '',
    childName: '',
    childAge: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Check if we're on the main page
  const isMainPage = routerLocation.pathname === '/' || routerLocation.pathname === '/gallery';
  
  // Use passed language or default
  const currentLang = language || (isMainPage ? 'en' : 'en');

  // Translation function with French support
  const t = (en, frOrId) => {
    if (isFrenchLocation && !isMainPage) {
      return currentLang === 'fr' ? frOrId : en;
    }
    return globalT(en, frOrId);
  };

  // Use Surabaya info for main page, location info for subpages
  const displayInfo = isMainPage ? schoolInfo : (location?.schoolInfo || {
    location: 'International',
    instagram: brandInfo.instagram,
    ageRange: '2-14'
  });
  
  const cityName = isMainPage ? 'Surabaya' : (location?.city || '');
  const ageRange = isMainPage ? '2-10' : (displayInfo.ageRange || '2-14');
  const [minAge, maxAge] = ageRange.split('-').map(Number);

  // UI Text for French
  const uiText = {
    getInTouch: { en: 'Get In Touch', fr: 'Contactez-Nous', id: 'Hubungi Kami' },
    startJourney: { 
      en: isMainPage ? 'Start Your Child\'s Ballet Journey' : `Start Your Child's Ballet Journey in ${cityName}`,
      fr: `Commencez le Parcours Ballet de Votre Enfant à ${cityName}`,
      id: isMainPage ? 'Mulai Perjalanan Balet Anak Anda' : `Mulai Perjalanan Balet Anak Anda di ${cityName}`
    },
    formIntro: {
      en: 'Ready to enroll your child or have questions? Fill out the form and we will get back to you as soon as possible. Prices available upon request.',
      fr: 'Prêt à inscrire votre enfant ou avez-vous des questions ? Remplissez le formulaire et nous vous répondrons dans les plus brefs délais. Prix disponibles sur demande.',
      id: 'Siap mendaftarkan anak Anda atau punya pertanyaan? Isi formulir dan kami akan menghubungi Anda sesegera mungkin. Harga tersedia berdasarkan permintaan.'
    },
    location: { en: 'Location', fr: 'Emplacement', id: 'Lokasi' },
    followInstagram: { en: 'Follow on Instagram', fr: 'Suivez sur Instagram', id: 'Ikuti di Instagram' },
    parentName: { en: 'Parent Name', fr: 'Nom du Parent', id: 'Nama Orang Tua' },
    childName: { en: 'Child Name', fr: 'Nom de l\'Enfant', id: 'Nama Anak' },
    childAge: { en: 'Child Age', fr: 'Âge de l\'Enfant', id: 'Usia Anak' },
    phone: { en: 'Phone/WhatsApp', fr: 'Téléphone/WhatsApp', id: 'Telepon/WhatsApp' },
    message: { en: 'Message', fr: 'Message', id: 'Pesan' },
    yourName: { en: 'Your name', fr: 'Votre nom', id: 'Nama Anda' },
    childsName: { en: 'Child\'s name', fr: 'Nom de l\'enfant', id: 'Nama anak' },
    questionsPlaceholder: { en: 'Any questions or special requests...', fr: 'Questions ou demandes spéciales...', id: 'Pertanyaan atau permintaan khusus...' },
    sending: { en: 'Sending...', fr: 'Envoi en cours...', id: 'Mengirim...' },
    sendInquiry: { en: 'Send Inquiry', fr: 'Envoyer la Demande', id: 'Kirim Pertanyaan' },
    thankYou: { en: 'Thank You!', fr: 'Merci !', id: 'Terima Kasih!' },
    received: { en: 'We have received your inquiry and will contact you soon.', fr: 'Nous avons reçu votre demande et vous contacterons bientôt.', id: 'Kami telah menerima pertanyaan Anda dan akan menghubungi Anda segera.' },
    success: { en: 'Thank you! We will contact you soon.', fr: 'Merci ! Nous vous contacterons bientôt.', id: 'Terima kasih! Kami akan menghubungi Anda segera.' },
    error: { en: 'Something went wrong. Please try again.', fr: 'Une erreur s\'est produite. Veuillez réessayer.', id: 'Terjadi kesalahan. Silakan coba lagi.' },
  };

  const getUI = (key) => {
    const texts = uiText[key];
    if (!texts) return key;
    if (isFrenchLocation && !isMainPage) {
      return currentLang === 'fr' ? texts.fr : texts.en;
    }
    return currentLang === 'en' ? texts.en : texts.id;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          parent_name: formData.parentName,
          child_name: formData.childName,
          child_age: formData.childAge,
          email: formData.email || null,
          phone: formData.phone,
          message: formData.message || null,
          location: cityName || 'General',
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      setIsSubmitted(true);
      toast.success(getUI('success'));

      // Reset form after 3 seconds
      setTimeout(() => {
        setFormData({
          parentName: '',
          childName: '',
          childAge: '',
          email: '',
          phone: '',
          message: '',
        });
        setIsSubmitted(false);
      }, 3000);
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error(getUI('error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-[#f7f5f2]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Contact Info */}
          <div>
            <span className="inline-block text-[#8B1C1C] font-semibold text-sm uppercase tracking-wider mb-4">
              {getUI('getInTouch')}
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1e1919] mb-6">
              {getUI('startJourney')}
            </h2>
            <p className="text-[#736c64] text-lg leading-relaxed mb-8">
              {getUI('formIntro')}
            </p>

            {/* Contact Details */}
            <div className="space-y-6 mb-8">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#8B1C1C]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-[#8B1C1C]" />
                </div>
                <div>
                  <p className="font-semibold text-[#1e1919]">{getUI('location')}</p>
                  <p className="text-[#736c64]">{displayInfo.location}</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#8B1C1C]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Instagram className="w-6 h-6 text-[#8B1C1C]" />
                </div>
                <div>
                  <p className="font-semibold text-[#1e1919]">Instagram</p>
                  <a
                    href={displayInfo.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#8B1C1C] hover:underline"
                  >
                    @studioballetizora
                  </a>
                </div>
              </div>
            </div>

            {/* CTA */}
            <a
              href={displayInfo.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#8B1C1C] text-white px-6 py-3 rounded-full font-semibold hover:bg-[#6d1616] transition-colors"
            >
              <Instagram className="w-5 h-5" />
              {getUI('followInstagram')}
            </a>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            {isSubmitted ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-12">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-[#1e1919] mb-2">
                  {getUI('thankYou')}
                </h3>
                <p className="text-[#736c64]">
                  {getUI('received')}
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="parentName">
                      {getUI('parentName')} *
                    </Label>
                    <Input
                      id="parentName"
                      name="parentName"
                      value={formData.parentName}
                      onChange={handleChange}
                      required
                      placeholder={getUI('yourName')}
                      className="rounded-lg"
                      data-testid="contact-parent-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="childName">
                      {getUI('childName')} *
                    </Label>
                    <Input
                      id="childName"
                      name="childName"
                      value={formData.childName}
                      onChange={handleChange}
                      required
                      placeholder={getUI('childsName')}
                      className="rounded-lg"
                      data-testid="contact-child-name"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="childAge">
                      {getUI('childAge')} *
                    </Label>
                    <Input
                      id="childAge"
                      name="childAge"
                      type="number"
                      min={minAge || 2}
                      max={maxAge || 14}
                      value={formData.childAge}
                      onChange={handleChange}
                      required
                      placeholder={ageRange}
                      className="rounded-lg"
                      data-testid="contact-child-age"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">
                      {getUI('phone')} *
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="+216..."
                      className="rounded-lg"
                      data-testid="contact-phone"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">
                    Email
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="email@example.com"
                    className="rounded-lg"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message">
                    {getUI('message')}
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder={getUI('questionsPlaceholder')}
                    rows={4}
                    className="rounded-lg resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="contact-submit-btn"
                  className="w-full bg-[#8B1C1C] hover:bg-[#6d1616] text-white py-6 rounded-full font-semibold transition-all"
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      {getUI('sending')}
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Send className="w-5 h-5" />
                      {getUI('sendInquiry')}
                    </span>
                  )}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
